import React, { useState, useEffect, useMemo } from 'react';
import { AgTableCard, Input, Button, PrimaryButton } from "../../UI";
import { CSVLink } from "react-csv";
import { getUsersDataByQuery, getAdobeUsers } from '../../../utils/APIs';
import { useUserContext } from '../../../contexts/UserContext';
import SaveTaskModal from '../Modal/SaveTaskModal';
import { useContentfulContext } from '../../../contexts/ContentfulContext';
import AgGridWrapper from '../../UI/AgGrid';
import { permission } from '../../../config/permission';
import AdobeUsersModal from '../Modal/AdobeUsersModal';
import PrimaryModal from '../../UI/Modal/PrimaryModal';
import { Search, Download, ChevronDown } from 'lucide-react';

const ListUsers = () => {
    const { userData, checkPermission } = useUserContext();
    const { content } = useContentfulContext();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [data, setData] = useState([]);
    const [gridApi, setGridApi] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [downloadDropdown, setDownloadDropdown] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [adobeUsers, setAdobeUsers] = useState([]);
    const [isGroupsModalOpen, setIsGroupsModalOpen] = useState(false);
    const [selectedUserForGroups, setSelectedUserForGroups] = useState(null);
    const [isAdobeUsersLoading, setIsAdobeUsersLoading] = useState(false);

    const handleEmailClick = (user) => {
        setSelectedUserForGroups(user);
        setIsGroupsModalOpen(true);
    };

    const fetchAdobeUsersList = async (force = false) => {
        if (!force && adobeUsers.length > 0) return;
        setIsAdobeUsersLoading(true);
        try {
            const res = await getAdobeUsers(userData?.token, userData?.selectedOrganization?.org_token, userData?.defaultAdobeIO?._id);
            if (res?.status) {
                setAdobeUsers(res.data);
            }
        } catch (error) {
            console.error("Failed to fetch Adobe users", error);
        } finally {
            setIsAdobeUsersLoading(false);
        }
    };

    const handleOpenManageUsers = () => {
        setIsModalOpen(true);
    };

    const hasManageAccess = userData?.selectedOrganization?.role?.some(item => 
        item.name.toLowerCase().includes("admin") || 
        item.name.toLowerCase().includes("owner")
    );

    useEffect(() => {
        const loadData = async () => {
            if(userData?.defaultAdobeIO) {
                setData([])
                setError("")
                await getUsersData()
                
                // Prefetch manage users list for the modal
                if (hasManageAccess) {
                    fetchAdobeUsersList(true);
                }
            }
        };
        loadData();
    }, [userData?.defaultAdobeIO?._id, userData?.selectedOrganization])

    const getUsersData = async () => {
        try {
            if(!userData?.defaultAdobeIO?.scope.includes("user_management_sdk")) {
                setError(content?.USER_MANAGEMENT_LIST_USERS_CONTENT.invalid_scope)
                return
            }

            setLoading(true);
            const result = await getUsersDataByQuery(
                userData?.token,
                userData?.selectedOrganization?.org_token,
                userData?.defaultAdobeIO?.x_API_Key,
                0,
                userData?.defaultAdobeIO?._id
            );
            setError("")
            setData(result.data.users);
        } catch (error) {
            setError(error?.message || content?.COMMON_MESSAGE.something_wrong_msg)
        } finally {
            setLoading(false);
        }
    }

    const columns = [
        {
            field: 'email',
            headerName: content.USER_MANAGEMENT_LIST_USERS_CONTENT.table_headers.email,
            sortable: true,
            filter: true,
            resizable: true,
            minWidth: 200,
            cellRenderer: ({ data }) => (
                <span 
                    className="text-blue-600 hover:text-blue-800 cursor-pointer hover:underline font-medium"
                    onClick={() => handleEmailClick(data)}
                >
                    {data.email}
                </span>
            )
        },
        {
            field: 'firstname',
            headerName: content.USER_MANAGEMENT_LIST_USERS_CONTENT.table_headers.firstName,
            sortable: true,
            filter: true,
            resizable: true,
            minWidth: 150,
        },
        {
            field: 'lastname',
            headerName: content.USER_MANAGEMENT_LIST_USERS_CONTENT.table_headers.lastName,
            sortable: true,
            filter: true,
            resizable: true,
            minWidth: 150,
        },
        {
            field: 'username',
            headerName: content.USER_MANAGEMENT_LIST_USERS_CONTENT.table_headers.username,
            sortable: true,
            filter: true,
            resizable: true,
            minWidth: 150,
        },
        {
            field: 'status',
            headerName: content.USER_MANAGEMENT_LIST_USERS_CONTENT.table_headers.status,
            sortable: true,
            filter: true,
            resizable: true,
            minWidth: 150,
        },
        {
            field: 'type',
            headerName: content.USER_MANAGEMENT_LIST_USERS_CONTENT.table_headers.type,
            sortable: true,
            filter: false,
            resizable: true,
            minWidth: 150,
        },
        {
            field: 'orgSpecific',
            headerName: content.USER_MANAGEMENT_LIST_USERS_CONTENT.table_headers.orgSpecific,
            sortable: true,
            filter: false,
            resizable: true,
            minWidth: 150,
            cellRenderer: params => params.value ? 'True' : 'False',
        },
        {
            field: 'country',
            headerName: content.USER_MANAGEMENT_LIST_USERS_CONTENT.table_headers.country,
            sortable: true,
            filter: false,
            resizable: true,
            minWidth: 150,
        },
        {
            field: 'domain',
            headerName: content.USER_MANAGEMENT_LIST_USERS_CONTENT.table_headers.domain,
            sortable: true,
            filter: false,
            resizable: true,
            minWidth: 150,
        }
    ];

    const handleSearch = (e) => {
        const val = typeof e === 'string' ? e : e?.target?.value || '';
        setSearchText(val);
        gridApi?.setQuickFilter(val);
    };

    const getFilteredData = () => {
        const filteredData = [];
        gridApi?.forEachNodeAfterFilterAndSort((node) => filteredData.push(node.data));
        return filteredData;
    };

    const onGridReady = (params) => {
        setGridApi(params.api);
    };

    const autoSizeStrategy = useMemo(() => {
        return {
            type: window.innerWidth < 1024 ? "fitCellContents" : "fitGridWidth",
            defaultMinWidth: 150,
            columnLimits: columns.map((col) => ({
                colId: col.field,
                minWidth: col.minWidth || 100,
            })),
        };
    }, [columns]);

    return (
        <div className="w-full">
            {/* Page Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight text-slate-900">{content.USER_MANAGEMENT_LIST_USERS_CONTENT.title}</h1>
                    <p className="text-sm text-slate-500 mt-1">{content.USER_MANAGEMENT_LIST_USERS_CONTENT.description}</p>
                </div>
                <div className="relative flex items-center gap-3">
                    {hasManageAccess && userData?.defaultAdobeIO?.scope?.includes("user_management_sdk") && (
                        <PrimaryButton handleClick={handleOpenManageUsers}>
                            {content?.USER_MANAGEMENT_LIST_USERS_CONTENT?.manage_adobe_users_btn || "Manage Adobe Users"}
                        </PrimaryButton>
                    )}
                    {checkPermission(permission.FUNCTIONS_ONE_OFF_TASK_CREATE) && data?.length > 0 && (
                        <SaveTaskModal feature_id={content.products.user_management.id} />
                    )}
                    {checkPermission(permission.FUNCTIONS_ONE_OFF_TASK_DOWNLOAD) && data?.length > 0 && (
                        <div className="relative">
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => setDownloadDropdown(!downloadDropdown)}
                                icon={<Download size={14} />}
                            >
                                <span>{content.USER_MANAGEMENT_LIST_USERS_CONTENT.download_csv_btn}</span>
                                <ChevronDown size={14} className="ml-1" />
                            </Button>
                            {downloadDropdown && (
                                <div className="z-10 bg-white divide-y divide-gray-100 rounded-lg shadow-lg border border-slate-200 w-44 absolute top-full right-0 mt-1">
                                    <ul className="py-2 text-sm text-gray-700">
                                        <li onClick={() => setDownloadDropdown(false)}>
                                            <CSVLink data={data} filename={`Adobe-Users-${new Date().getTime()}`} className="block px-4 py-2 hover:bg-slate-50">
                                                {content.USER_MANAGEMENT_LIST_USERS_CONTENT.all}
                                            </CSVLink>
                                        </li>
                                        <li onClick={() => setDownloadDropdown(false)}>
                                            <CSVLink data={getFilteredData()} filename={`Adobe-Users-${new Date().getTime()}`} className="block px-4 py-2 hover:bg-slate-50">
                                                {content.USER_MANAGEMENT_LIST_USERS_CONTENT.filtered}
                                            </CSVLink>
                                        </li>
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Filter and Table Container */}
            <AgTableCard
                header={
                    <div className="w-full sm:w-80">
                        <Input
                            prefixIcon={<Search size={15} />}
                            placeholder={content?.USER_MANAGEMENT_LIST_USERS_CONTENT?.search_placeholder || "Search User name"}
                            value={searchText}
                            onChange={handleSearch}
                            clearable
                        />
                    </div>
                }
                tableId="list-users-table"
                loading={loading}
                emptyMessage={content.USER_MANAGEMENT_LIST_USERS_CONTENT.no_data_found_msg}
                rowData={data || []}
                columnDefs={columns}
                defaultColDef={{
                    sortable: true,
                    filter: true,
                    resizable: true,
                }}
                autoSizeStrategy={autoSizeStrategy}
                onGridReady={onGridReady}
            >
                {error ? (
                    <h3 className="text-center py-6 text-rose-600 font-medium">{error}</h3>
                ) : null}
            </AgTableCard>

            {hasManageAccess && isModalOpen && (
                <AdobeUsersModal 
                    isOpen={isModalOpen} 
                    onClose={() => setIsModalOpen(false)} 
                    users={adobeUsers}
                    setUsers={setAdobeUsers}
                    isLoading={isAdobeUsersLoading}
                    refreshUsers={() => fetchAdobeUsersList(true)}
                />
            )}

            {isGroupsModalOpen && selectedUserForGroups && (
                <PrimaryModal
                    hideModal={() => {
                        setIsGroupsModalOpen(false);
                        setSelectedUserForGroups(null);
                    }}
                    title={`Associated Groups for ${selectedUserForGroups.email}`}
                    widthClass="max-w-3xl"
                >
                    <div className="p-6">
                        <div className="mb-4">
                            <p className="text-sm text-gray-500">
                                <strong>User:</strong> {selectedUserForGroups.firstname} {selectedUserForGroups.lastname} ({selectedUserForGroups.username})
                            </p>
                        </div>
                            <AgGridWrapper
                                tableId="user-groups-table"
                                rowData={(selectedUserForGroups.groups || []).map(g => ({ name: g }))}
                                columnDefs={[
                                    {
                                        headerName: 'Group Name',
                                        field: 'name',
                                        flex: 1,
                                        filter: true,
                                        sortable: true,
                                        resizable: true
                                    }
                                ]}
                                defaultColDef={{
                                    sortable: true,
                                    filter: true,
                                    resizable: true,
                                }}
                                autoSizeStrategy={{
                                    type: "fitGridWidth"
                                }}
                            />
                        {(selectedUserForGroups.groups || []).length === 0 && (
                            <p className="text-center text-sm text-gray-400 mt-4">No associated groups found for this user.</p>
                        )}
                    </div>
                </PrimaryModal>
            )}
        </div>
    );
};

export default ListUsers;
